// Utility functions for sharing repair orders

/**
 * Generate the public URL for a repair order
 * @param repairOrderId - The ID of the repair order
 * @param baseUrl - Optional base URL, defaults to window.location.origin
 * @returns The full public URL
 */
export const generatePublicRepairOrderUrl = (repairOrderId: string, baseUrl?: string): string => {
  const base = baseUrl || window.location.origin;
  return `${base}/public/repair-order/${repairOrderId}`;
};

/**
 * Generate WhatsApp share URL with prefilled message
 * @param repairOrderNumber - The repair order number (e.g., "RO-12345")
 * @param publicUrl - The public URL to share
 * @param phoneNumber - Optional phone number to share to
 * @returns WhatsApp URL
 */
export const generateWhatsAppShareUrl = (
  repairOrderNumber: string,
  publicUrl: string,
  phoneNumber?: string
): string => {
  const message = `Your repair order ${repairOrderNumber} is ready. View details: ${publicUrl}`;
  const encodedMessage = encodeURIComponent(message);

  if (phoneNumber) {
    // Remove any non-numeric characters from phone number
    const cleanPhone = phoneNumber.replace(/\D/g, '');
    return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
  }

  return `https://wa.me/?text=${encodedMessage}`;
};

/**
 * Generate email share URL with subject and body
 * @param repairOrderNumber - The repair order number
 * @param publicUrl - The public URL to share
 * @param customerEmail - Optional customer email address
 * @returns Mailto URL
 */
export const generateEmailShareUrl = (
  repairOrderNumber: string,
  publicUrl: string,
  customerEmail?: string
): string => {
  const subject = encodeURIComponent('Your Repair Order Details');
  const body = encodeURIComponent(`Please find your repair order details: ${publicUrl}`);
  const email = customerEmail ? `&to=${encodeURIComponent(customerEmail)}` : '';

  return `mailto:?subject=${subject}&body=${body}${email}`;
};

/**
 * Copy text to clipboard
 * @param text - Text to copy
 * @returns Promise that resolves when copy is complete
 */
export const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // Fallback for older browsers
    const textArea = document.createElement('textarea');
    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
  }
};

/**
 * Check if device supports sharing (Web Share API)
 * @returns Boolean indicating if Web Share API is supported
 */
export const supportsWebShare = (): boolean => {
  return typeof navigator !== 'undefined' && 'share' in navigator;
};

/**
 * Use Web Share API to share content
 * @param title - Share title
 * @param text - Share text
 * @param url - Share URL
 * @returns Promise that resolves when share is complete
 */
export const webShare = async (title: string, text: string, url: string): Promise<void> => {
  if (!supportsWebShare()) {
    throw new Error('Web Share API not supported');
  }

  await navigator.share({
    title,
    text,
    url,
  });
};